import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Loader2, CheckCircle2, XCircle, ArrowRight } from "lucide-react";
import { paymentStatus } from "@/lib/api";
import { Seo } from "@/seo/Seo";

export default function PaymentReturn() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const isCancel = window.location.pathname.endsWith("/cancel");
  const token = params.get("token") || "";
  const sessionId = params.get("session_id") || "";
  const [state, setState] = useState(isCancel ? "cancelled" : "checking");

  useEffect(() => {
    if (isCancel || !sessionId) return;
    let tries = 0;
    let active = true;
    const poll = async () => {
      try {
        const r = await paymentStatus(sessionId);
        if (!active) return;
        if (r.status === "succeeded") return setState("success");
        if (r.status === "failed") return setState("failed");
      } catch (_) {}
      tries += 1;
      if (tries < 15 && active) setTimeout(poll, 2000);
      else if (active) setState("pending");
    };
    poll();
    return () => { active = false; };
  }, [sessionId, isCancel]);

  const goWorkspace = () => navigate(token ? `/workspace?token=${token}` : "/");

  const Icon = state === "success" ? CheckCircle2 : state === "checking" ? Loader2 : XCircle;
  const color = state === "success" ? "text-cyan-400" : state === "failed" ? "text-red-400" : "text-zinc-400";
  const title = {
    checking: "Confirming your payment…",
    success: "Payment received.",
    failed: "Payment could not be completed.",
    cancelled: "Payment cancelled.",
    pending: "Payment is still processing.",
  }[state];
  const sub = {
    checking: "Please wait while we confirm with our payment provider.",
    success: "Thank you. Your engagement is moving forward.",
    failed: "No charge was completed. You can try again from your workspace.",
    cancelled: "No charge was made. You can return and pay when ready.",
    pending: "We'll confirm shortly. You can check back in your workspace.",
  }[state];

  return (
    <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center px-6" data-testid="payment-return">
      <Seo noindex path="/payment/success" title="Payment · AIROTECH" />
      <div className="max-w-md text-center">
        <Icon className={`h-12 w-12 mx-auto ${color} ${state === "checking" ? "animate-spin" : ""}`} />
        <h1 className="mt-6 font-heading font-extrabold tracking-tighter text-3xl" data-testid="payment-return-title">{title}</h1>
        <p className="mt-3 text-zinc-400">{sub}</p>
        {state !== "checking" && (
          <button data-testid="payment-return-workspace" onClick={goWorkspace}
            className="mt-8 inline-flex items-center gap-2 bg-white text-black font-medium px-7 py-3.5 rounded-full mono-label hover:bg-cyan-400 transition-colors">
            Back to my workspace <ArrowRight className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
}
